import styled from "styled-components"
import { Link } from "react-router-dom"

type StyledProps = {
  $blocked?: boolean
  $expanded?: boolean
  $sideBarCollapsed?: boolean
  $padding?: string
}

export const Container = styled.div`
  margin-top: 3rem;
`

export const Email = styled.h3`
  color: ${({ theme }) => theme.colors.turquoiseDark};
`

export const InfoText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 2rem;

  h3 {
    margin: 0 0.3rem 0;
  }

  svg {
    height: 1.8rem;
    margin-right: 0.5rem;
  }

  ${(p) => p.theme.mQ.MEDIA_MIN_MEDIUM} {
    flex-direction: row;
  }
`

export const TableHeader = styled.div<StyledProps>`
  margin-top: 1rem;
  display: grid;
  background-color: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid ${({ theme }) => theme.colors.borderLight};
  border-left: none;
  grid-template-columns: 2fr 1fr 1fr;
  border-bottom: ${(p) => !p.$expanded && "none"};

  ${(p) => p.theme.mQ.MEDIA_MIN_LARGE} {
    grid-template-columns: ${(p) => (p.$sideBarCollapsed ? "4fr 2.5fr 2fr 2fr" : "4fr 2.5fr 2fr")};
  }

  ${(p) => p.theme.mQ.MEDIA_MIN_X_LARGE} {
    grid-template-columns: 4fr 2.5fr 1.5fr 1.5fr 1fr;
  }
`

export const Section = styled.div`
  font-weight: 700;
  padding: 1rem;
  border-left: 0.1rem solid ${({ theme }) => theme.colors.borderLight};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 7rem;
`

export const IconWrapper = styled.div`
  border-right: 0.1rem solid ${({ theme }) => theme.colors.borderLight};
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  span {
    border-radius: 50%;
    padding: 1rem;
    width: 4rem;
    height: 4rem;
    margin-right: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

export const BlockingIcon = styled.span`
  background: ${({ theme }) => theme.colors.errorBackground};
  border: 0.1rem solid ${({ theme }) => theme.colors.errorText};

  svg {
    height: 1.6rem;
    fill: ${({ theme }) => theme.colors.errorText};
  }
`

export const InformativeIcon = styled.span`
  background: ${({ theme }) => theme.colors.yellowLight};
  border: 0.1rem solid ${({ theme }) => theme.colors.orange};

  svg {
    height: 1.6rem;
    fill: ${({ theme }) => theme.colors.orange};
  }
`

export const UserIcon = styled.div`
  background: ${({ theme }) => theme.colors.turquoiseDark};
  font-size: 1.3rem;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.white};
  border-radius: 0.75rem;
  border: none;
  height: 3rem;
  width: 3rem;
  margin-right: 1.5rem;
  margin-left: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
`

export const Comment = styled.div`
  margin-left: 2rem;
`

export const OrderHeader = styled(Section)`
  display: flex;
  justify-self: flex-start;
`

export const TableRow = styled(TableHeader)`
  margin-top: 0;

  div {
    font-weight: 400;
  }
`
export const MissingRow = styled(TableRow)`
  padding-right: 6rem;
  border-top: none;
  border-bottom: none;
  grid-template-columns: 1fr;

  ${(p) => p.theme.mQ.MEDIA_MIN_MEDIUM} {
    margin-left: 6rem;
  }
`

export const ActionRow = styled(TableRow)`
  padding-right: 6rem;
  border-top: none;
  border-bottom: 0.1rem solid ${(p) => p.theme.colors.borderLight};
  grid-template-columns: 1fr;
  &:last-child {
    border-bottom: none;
  }

  ${(p) => p.theme.mQ.MEDIA_MIN_MEDIUM} {
    margin-left: 6rem;
  }

  ${(p) => p.theme.mQ.MEDIA_MIN_LARGE} {
    grid-template-columns: 7fr 5fr;
    padding-right: 4.3rem;
  }

  ${(p) => p.theme.mQ.MEDIA_MIN_X_LARGE} {
    grid-template-columns: 9fr 3fr;
    padding-right: 2rem;
  }
`

export const Reference = styled(Section)`
  display: flex;
  justify-self: flex-start;
`

export const ReferenceText = styled(Link)`
  cursor: pointer;
  position: relative;
  font-weight: bold;
  color: inherit;
  text-decoration: none;

  &:after {
    content: "";
    width: 0;
  }

  &:hover {
    &:after {
      transition: 0.4s width ease-out;
      content: "";
      width: 100%;
      background: black;
      height: 0.1rem;
      position: absolute;
      bottom: 0;
      left: 0;
    }
  }

  &:visited {
    color: inherit;
  }
`

export const SmallInfo = styled.div`
  font-size: 1.1rem;
  line-height: 1.5rem;
`

export const ExpandButton = styled.span<StyledProps>`
  width: 5rem;
  border-right: 0.1rem solid ${({ theme }) => theme.colors.borderLight};
  margin-right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 5rem;
  padding: ${p => p.$padding}
  cursor: pointer;

  svg {
    transition: transform 0.5s ease-in-out;
    height: 1.8rem;
    transform: ${({ $expanded }) => $expanded && "rotate(180deg)"};
  }
`

export const DateTime = styled(Section)`
  display: flex;
  justify-content: flex-end;

  div {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    span {
      line-height: 1.9rem;
      display: flex;
      justify-content: flex-end;
    }
  }
`

export const StateDate = styled(DateTime)`
border: none;
`

export const StatusContainer = styled.span<StyledProps>`
  background-color: ${({ theme, $blocked }) =>
    $blocked ? theme.colors.yellowLight : theme.colors.turquoiseLighter};
  border: 0.1rem solid
    ${({ theme, $blocked }) => ($blocked ? theme.colors.yellowDark : theme.colors.turquoiseDark)};
  border-radius: 50%;
  height: 4rem;
  width: 4rem;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    fill: ${({ theme, $blocked }) =>
      $blocked ? theme.colors.yellowDark : theme.colors.turquoiseDark};
    height: 1.6rem;
  }
`
